import React, { useEffect } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { CheckCircle, ArrowRight, Phone, EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";
import { BRAND } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";

/**
 * Shared confirmation page shown after a lead submits either the Contact
 * or the Quote form. Reads context from `useLocation().state` so it can
 * personalise the thank-you (first name, email it's been logged against,
 * source: 'contact' vs 'quote').
 *
 * If someone navigates here directly with no state (e.g. they refreshed
 * the page or bookmarked the URL), we send them to the homepage rather
 * than show a confused empty state.
 */
export default function ThankYou() {
  usePageMeta({
    title: "Request received",
    path: "/thank-you",
    description: "Thanks for getting in touch with True North Maintenance — we'll get back to you shortly.",
  });
  const loc = useLocation();
  const navigate = useNavigate();
  const state = loc.state || {};
  const { name = "", email = "", source = "contact" } = state;

  // Fire a Meta Pixel `Lead` conversion event as the confirmation page renders
  // — this is the most reliable signal of an actual completed lead and lets
  // the user run conversion-optimised ad campaigns.
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "Lead", { source });
    }
    // Scroll to top so the hero is in view on mobile.
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [source]);

  // Defensive: if someone lands here with no state at all, send them home
  // after a tick so the route isn't reachable as a permanent bookmark.
  if (!email && !name) {
    return <Navigate to="/" replace />;
  }

  const firstName = (name || "").split(" ")[0] || "there";
  const isQuote = source === "quote";

  return (
    <div data-testid="thank-you-page">
      <section
        data-nav-theme="dark"
        className="bg-navy-deep text-white min-h-screen flex items-center px-5 md:px-10 relative overflow-hidden"
      >
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="relative max-w-2xl mx-auto w-full py-32">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 border border-white/20 mb-8">
            <CheckCircle size={32} weight="fill" className="text-white" />
          </div>

          <div className="eyebrow !text-white/55">
            {isQuote ? "Quote request received" : "Message received"}
          </div>

          <h1 className="font-display display-lg tracking-wide mt-3 leading-tight">
            Thanks, {firstName}.
          </h1>

          <p className="mt-6 text-slate-300 text-base md:text-lg leading-relaxed max-w-xl">
            We've got your {isQuote ? "quote request" : "message"} and one of the team
            will get back to you shortly{email ? <> at <span className="text-white font-semibold">{email}</span></> : ""}.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-3">
            <a
              href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
              data-testid="thanks-call-cta"
              className="flex items-center gap-3 p-5 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors rounded-sm group"
            >
              <Phone size={20} weight="fill" className="text-white" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Need it urgent?</div>
                <div className="font-display text-lg tracking-wide text-white mt-0.5">{BRAND.phone}</div>
              </div>
            </a>
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="thanks-email-cta"
              className="flex items-center gap-3 p-5 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors rounded-sm group"
            >
              <EnvelopeSimple size={20} weight="fill" className="text-white" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Add photos / details</div>
                <div className="text-sm text-white mt-0.5 truncate">{BRAND.email}</div>
              </div>
            </a>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between border-t border-white/10 pt-8">
            <button
              type="button"
              onClick={() => navigate("/")}
              data-testid="thanks-home-cta"
              className="inline-flex items-center gap-2 text-sm text-white/75 hover:text-white transition-colors"
            >
              <ArrowLeft size={14} weight="bold" />
              Back to homepage
            </button>
            <Link
              to="/gallery"
              data-testid="thanks-gallery-cta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-white group"
            >
              See some of our recent work
              <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
